import { MemoStatefulAutocompleteWithText } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { ReactElement } from 'react';
import { useStyles as useFormStyles } from 'components/common/forms/formStyles/styles';
import { useStyles } from './styles';
import cn from 'classnames';
import Divider from '@mui/material/Divider';
import { CircularProgress } from '@mui/material';
import { ButtonText, ButtonContained } from 'components/general';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

import { useCurrentUserRole } from 'store/user';
import { DocumentationInformation } from '../DocumentationInformation';
import { DocumentationFile } from 'store/documentation/model/types';
import { FormFieldProps } from 'components/inspection/Form/types';
import { MemoPreviewField } from 'components/general/PreviewField';
import { FormattedMessage, formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';

interface SetableDocumentationFields {
  name: string;
  number: string;
}

export function DocumentationForm({
  onCancel,
  onSubmit,
  isPosting,
  file,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  isPosting: boolean;
  file: DocumentationFile;
}): ReactElement {
  const formClasses = useFormStyles();
  const role = useCurrentUserRole();
  const classes = useStyles();
  const intl = useIntl();
  const currentVersionNumber =
    formatVersionNumber(file.currentVersionNumber) ??
    formatTranslation(intl, DocumentationTranslationKeys.noCurrentVersion);

  const nameField: FormFieldProps<SetableDocumentationFields> = {
    formKey: `name`,
    required: true,
    labelId: DocumentationTranslationKeys.nameFieldLabel,
    fieldName: `documentation-name`,
    localStorageKey: 'documentation-name',
    reserveSpaceForHelperText: false,
    dense: true,
    'data-qa': 'documentation_name_field',
    multiline: true,
    issueParams: null,
  };

  const numberField: FormFieldProps<SetableDocumentationFields> = {
    formKey: `number`,
    required: false,
    labelId: DocumentationTranslationKeys.numberFieldLabel,
    fieldName: `documentation-number`,
    localStorageKey: `documentation-number`,
    reserveSpaceForHelperText: false,
    dense: true,
    'data-qa': 'documentation_number_field',
    issueParams: null,
  };
  // TODO upgrade save button on this: https://mui.com/material-ui/react-button/#loading-button
  return (
    <div className={classes.root}>
      <div className={cn(formClasses.root, classes.form)}>
        <div className='row'>
          <MemoStatefulAutocompleteWithText {...nameField} />
        </div>
        <div className='row'>
          <MemoStatefulAutocompleteWithText {...numberField} />
        </div>
        {file.currentVersionNumber !== undefined && (
          <div className='row'>
            <MemoPreviewField
              label={formatTranslation(intl, DocumentationTranslationKeys.currentVersionNumberLabel)}
              value={currentVersionNumber}
              disabled={true}
              dense={true}
            />
          </div>
        )}

        <DocumentationInformation role={role} file={file} />
      </div>
      <Divider />
      <div className={classes.submitButtons}>
        <ButtonText onClick={onCancel} disabled={isPosting}>
          <FormattedMessage id={CommonTranslationKeys.cancel} />
        </ButtonText>
        <ButtonContained
          onClick={onSubmit}
          disabled={isPosting}
          endIcon={
            isPosting ? (
              <CircularProgress
                size={13}
                thickness={4}
                disableShrink
                color='secondary'
              />
            ) : undefined
          }
        >
          <FormattedMessage id={CommonTranslationKeys.save} />
        </ButtonContained>
      </div>
    </div>
  );
}
